import { useEffect, useRef, useCallback, useState } from 'react';

/**
 * Hook para detectar inactividad en modo Kiosk
 * Rastrea la actividad del usuario (clicks, toques, movimiento del mouse)
 * y ejecuta una función callback cuando se detecta inactividad
 * 
 * @param {number} inactivityTime - Tiempo en ms antes de mostrar advertencia (ej: 5 minutos = 300000)
 * @param {function} onInactivityWarning - Callback cuando se detecta inactividad
 * @param {function} onLogout - Callback cuando se debe desloguear
 * @param {boolean} enabled - Si el hook está habilitado
 */
export const useInactivityTimer = (
//   inactivityTime = 300000, // 5 minutos por defecto
    inactivityTime = 5000, // 5 segundos por defecto
  onInactivityWarning = () => {},
  onLogout = () => {},
  enabled = true
) => {
  const inactivityTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const [isWarningActive, setIsWarningActive] = useState(false);

  const resetInactivityTimer = useCallback(() => {
    // Limpiar timers previos
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }

    if (!enabled) return;

    // Timer de inactividad: después de X tiempo, mostrar advertencia
    inactivityTimerRef.current = setTimeout(() => {
      console.log('⏰ Inactividad detectada - mostrando advertencia');
      setIsWarningActive(true);
      onInactivityWarning();

      // Timer de desconexión: si no hay respuesta en 30 segundos, desloguear
      warningTimerRef.current = setTimeout(() => {
        console.log('❌ Tiempo de confirmación expirado - deslogueando');
        setIsWarningActive(false);
        onLogout();
      }, 30000); // 30 segundos para confirmar
    }, inactivityTime);
  }, [inactivityTime, onInactivityWarning, onLogout, enabled]);

  const dismissWarning = useCallback(() => {
    console.log('✅ Usuario confirmó que sigue ahí');
    setIsWarningActive(false);

    // Limpiar el timer de desconexión
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }

    // Reiniciar el timer de inactividad
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  useEffect(() => {
    if (!enabled) return;

    // Eventos que resetan el contador de inactividad
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    const handleActivity = () => {
      // Si hay una advertencia activa y el usuario interactúa, descartar la advertencia
      if (isWarningActive) {
        dismissWarning();
      } else {
        // Si no hay advertencia, reiniciar el timer normalmente
        resetInactivityTimer();
      }
    };

    // Agregar listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity);
    });

    // Iniciar el timer la primera vez
    resetInactivityTimer();

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });

      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
      }
    };
  }, [enabled, isWarningActive, resetInactivityTimer, dismissWarning]);

  return {
    isWarningActive,
    dismissWarning,
    forceLogout: onLogout
  };
};

